/**
 * The employee page's Vakter tab (owner brief, 2026-09-21): what is coming and what happened, one
 * row per shift. A past shift says what it is at month close in the same words as Timeliste
 * (timeliste/close.ts), so the two pages never disagree. A row opens the shift's own drawer.
 */
import { errorText } from '@/api/client';
import { type ApiShift, type ApiUserFull, useShiftTypes, useShiftWrites, useShifts } from '@/api/data';
import { T } from '@/i18n';
import { breakPolicy, dayEntries, oslo, rulePerson, tenantRules, today, worked } from '@/rules';
import { useTenant } from '@/shell/tenant';
import { Button, Chip, Empty, Skeleton, toast } from '@/ui';
import { DateJump } from '@/ui/DateJump';
import { DN, MONTHS, fmtDs } from '@/ui/format';
import { Icon } from '@/ui/icons';
import { addDays, dow } from '@timefly/core';
import { useState } from 'react';
import { deviationShort, kindOf } from '../timeliste/close';
import { lastDayOf, monthOf, prevMonth } from '../timeliste/summary';
import { type DraftShift, ShiftDrawer, draftOf, newDraft } from '../vaktplan/ShiftDrawer';
import { hm } from './summary';

type View = 'kommende' | 'historikk' | 'alle';
const dateOf = (s: ApiShift) => oslo(s.start_time)?.date ?? '';
const clock = (iso: string | null | undefined) => oslo(iso)?.time?.slice(0, 5) ?? '';
const nextMonth = (m: string) => {
  const [y, mo] = m.split('-').map(Number) as [number, number];
  const d = new Date(Date.UTC(y, mo, 1));
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
};
const monthName = (m: string) => {
  const s = `${MONTHS[Number(m.slice(5, 7)) - 1] ?? ''} ${m.slice(0, 4)}`;
  return s.charAt(0).toUpperCase() + s.slice(1);
};

export function VakterTab({ u }: { u: ApiUserFull }) {
  const { tenant } = useTenant();
  const rules = tenantRules();
  const day0 = today();
  const [view, setView] = useState<View>('kommende');
  const [month, setMonth] = useState(monthOf(day0));
  const [draft, setDraft] = useState<DraftShift | null>(null);
  // kommende: the next six weeks from today; historikk and alle: the chosen month
  const from = view === 'kommende' ? day0 : `${month}-01`;
  const to = view === 'kommende' ? addDays(day0, 42) : lastDayOf(month);
  const shiftsQ = useShifts(addDays(from, -1), addDays(to, 1));
  const typesQ = useShiftTypes();
  const w = useShiftWrites();
  const now = Date.now();
  const policy = breakPolicy(tenant);
  const all = (shiftsQ.data?.shifts ?? []).filter((s) => {
    const d = dateOf(s);
    return s.user_id === u.id && d >= from && d <= to;
  });
  const rows = all
    .filter((s) =>
      view === 'kommende'
        ? Date.parse(s.end_time) > now
        : view === 'historikk'
          ? Date.parse(s.end_time) <= now
          : true,
    )
    .sort((a, b) =>
      view === 'historikk'
        ? b.start_time.localeCompare(a.start_time)
        : a.start_time.localeCompare(b.start_time),
    );
  // one heading per month, so a six-week list reads as "September", then "Oktober"
  const groups: { m: string; list: ApiShift[] }[] = [];
  for (const s of rows) {
    const m = monthOf(dateOf(s));
    const g = groups[groups.length - 1];
    if (g && g.m === m) g.list.push(s);
    else groups.push({ m, list: [s] });
  }
  const person = rulePerson(u, tenant);
  const drawerShift = draft?.id != null ? all.find((s) => s.id === draft.id) : undefined;

  const status = (s: ApiShift) => {
    const k = kindOf(s, now, rules.toleranceMin ?? 15);
    if (k === 'approved')
      return (
        <Chip tone="ok" small>
          {T('Godkjent')}
        </Chip>
      );
    if (k === 'ready')
      return (
        <Chip tone="neu" small>
          {T('Klar')}
        </Chip>
      );
    if (k === 'deviation') return <Chip tone="warn" small>{`${T('Avvik')} ${deviationShort(s) ?? ''}`}</Chip>;
    if (k === 'missing')
      return (
        <Chip tone="dgr" small>
          {T('Mangler stempling')}
        </Chip>
      );
    if (k === 'open')
      return (
        <Chip tone="dgr" small>
          {T('Ikke stemplet ut')}
        </Chip>
      );
    if (k === 'running')
      return (
        <Chip tone="ok" small>
          {T('Pågår')}
        </Chip>
      );
    return s.published ? null : (
      <Chip tone="neu" small>
        {T('Utkast')}
      </Chip>
    );
  };

  return (
    <section className="sec">
      <div className="vtbar">
        <div className="seg">
          {(['kommende', 'historikk', 'alle'] as const).map((v) => (
            <button key={v} type="button" aria-pressed={view === v} onClick={() => setView(v)}>
              {v === 'kommende' ? T('Kommende') : v === 'historikk' ? T('Historikk') : T('Alle vakter')}
            </button>
          ))}
        </div>
        {view !== 'kommende' && (
          <div className="wk" aria-label={T('Måned')}>
            <button type="button" aria-label={T('Forrige måned')} onClick={() => setMonth(prevMonth(month))}>
              <Icon name="chevronLeft" />
            </button>
            <DateJump month value={month} onChange={setMonth}>
              <span className="lbl">{monthName(month)}</span>
            </DateJump>
            <button type="button" aria-label={T('Neste måned')} onClick={() => setMonth(nextMonth(month))}>
              <Icon name="chevronRight" />
            </button>
          </div>
        )}
        <span className="sp" />
        <Button
          onClick={() =>
            setDraft(newDraft(view === 'kommende' ? day0 : `${month}-01`, u.id, typesQ.data?.[0]))
          }
        >
          <Icon name="plus" />
          {T('Legg til vakt')}
        </Button>
      </div>
      <div className="card2">
        {shiftsQ.isPending ? (
          <Skeleton rows={5} />
        ) : !rows.length ? (
          <Empty>
            {view === 'kommende'
              ? T('Ingen vakter de neste seks ukene.')
              : T('Ingen vakter i {m}.', { m: monthName(month).toLowerCase() })}
          </Empty>
        ) : (
          groups.map((g) => (
            <div key={g.m}>
              {groups.length > 1 && <h3 className="vtm">{monthName(g.m)}</h3>}
              {g.list.map((s) => {
                const d = dateOf(s);
                const past = Date.parse(s.end_time) <= now;
                const tasks = s.taskAssignments?.length ?? 0;
                return (
                  <button type="button" key={s.id} className="vtrow" onClick={() => setDraft(draftOf(s))}>
                    <span className="vtd">
                      {DN[dow(d)]} {fmtDs(d)}
                    </span>
                    <span className="mono">
                      {clock(s.start_time)}–{clock(s.end_time)}
                    </span>
                    <span className="vtt">{s.shiftType?.name ?? T('Vakt')}</span>
                    <span className="mono vtp">
                      {s.punch_in_time
                        ? `${clock(s.punch_in_time)}–${s.punch_out_time ? clock(s.punch_out_time) : '?'}`
                        : ''}
                    </span>
                    <span className="mono vth">{past && s.punch_out_time ? hm(worked(s, policy)) : ''}</span>
                    <span className="vts">
                      {tasks > 0 && (
                        <Chip tone="neu" small>
                          {tasks} {tasks === 1 ? T('oppgave') : T('oppgaver')}
                        </Chip>
                      )}
                      {status(s)}
                    </span>
                  </button>
                );
              })}
            </div>
          ))
        )}
      </div>
      <ShiftDrawer
        draft={draft}
        tasks={drawerShift?.taskAssignments}
        entries={drawerShift ? dayEntries(drawerShift, person, rules) : []}
        types={typesQ.data ?? []}
        users={[u]}
        onClose={() => setDraft(null)}
        onDelete={(id, reason) =>
          w.remove.mutateAsync({ id, reason }).then(
            () => {
              toast(T('Vakten er slettet'));
              setDraft(null);
            },
            (e) => toast(errorText(e)),
          )
        }
      />
    </section>
  );
}
